/**
 * Block 12 PR-H1 Round 4 — declarative composition whitelist + rules per anketa-type.
 *
 * Aibek's правила: для каждого из 8 `CalculatorAnketaType` определён набор
 * composition rows которые могут seeded в КП Spec, и для каждой rule —
 * data source (material payload field или encoded freeItem prefix).
 *
 * `seedFromAnketa.deriveCompositionFromAnketa` iterates `COMPOSITION_RULES[anketaType]`
 * для каждой defaultRow из `DEFAULT_COMPOSITION_ROWS`: если rule match по rowId
 * → проверяет condition (price>0 для material-payload, qty>0 AND price>0 для
 * free-item) → seeds row с itemType/brand. Иначе row остаётся blank,
 * `isCompositionRowEmpty` filter в КП Spec скрывает.
 *
 * **Brand mapping:** brand берётся из `project.data.brandSelections[rule.anketaRowId ?? rule.rowId]`.
 * `anketaRowId` нужен только для случая mismatch composition rowId ↔ anketa-side id
 * (vitrine composition ↔ aluminum-glass-vitrine additional). Для остальных — same id.
 *
 * **`fittings` row отсутствует ни в одном whitelist** — Aibek (Round 4) подтвердил
 * что Фурнитура никогда не seeded автоматически, заполняется вручную в КП Spec.
 *
 * Reference: TZ Block_12_PR_H1_hotfix_round4_start.md TL;DR table.
 */

import type { CalculatorAnketaType } from '@pomoshnik/shared';

/**
 * Decoration rule per composition row: где seedFromAnketa берёт condition data.
 *
 * - `'material-payload'` — условие проверяется по dedicated payload field
 *   (только facades через `payload.facadePrice`). Condition: `price > 0`.
 * - `'free-item'` — условие проверяется по encoded freeItem в `payload.freeItems`
 *   с заданным `freeItemPrefix`. Condition: `qty > 0 AND price > 0`.
 */
export interface CompositionRule {
  /** Composition row id (matches DEFAULT_COMPOSITION_ROWS, kebab-case slug). */
  rowId: string;
  /** Data source category — выбирает condition check + lookup path. */
  source: 'material-payload' | 'free-item';
  /**
   * Для `source: 'free-item'` — prefix в freeItem.name для match.
   * Encoded `useAnketaPersist.encodeAuxiliaryRows` (Section Б/В/Г/Д).
   */
  freeItemPrefix?: string;
  /**
   * Для `source: 'material-payload'` — поле в `project.data` для price check
   * (e.g., `'facadePrice'`).
   */
  materialPriceField?: string;
  /**
   * Anketa-side rowId для brandSelections lookup, если != composition rowId.
   * Используется для composition `vitrine` ↔ anketa additional `aluminum-glass-vitrine`.
   * undefined → используется rowId напрямую (большинство rows).
   */
  anketaRowId?: string;
}

/**
 * Per-anketa-type whitelist composition rows. 8 entries (соответствие
 * `CALCULATOR_ANKETA_TYPES`). 'other' имеет empty array — пустая Anketa
 * не seed'ит ни одной row.
 */
export const COMPOSITION_RULES: Record<CalculatorAnketaType, ReadonlyArray<CompositionRule>> = {
  kitchen: [
    { rowId: 'countertop', source: 'free-item', freeItemPrefix: 'Столешница' },
    { rowId: 'facades', source: 'material-payload', materialPriceField: 'facadePrice' },
    { rowId: 'facade-radius', source: 'free-item', freeItemPrefix: 'Фасад радиусный' },
    {
      rowId: 'vitrine',
      source: 'free-item',
      freeItemPrefix: 'Витрина в алюминиевом',
      anketaRowId: 'aluminum-glass-vitrine',
    },
    { rowId: 'metallic-legs', source: 'free-item', freeItemPrefix: 'Металлические опоры' },
    { rowId: 'basket-pullout', source: 'free-item', freeItemPrefix: 'Корзина выдвижная' },
    { rowId: 'foldable-facade', source: 'free-item', freeItemPrefix: 'Складной фасад' },
    { rowId: 'trash-bin', source: 'free-item', freeItemPrefix: 'Мусорное ведро' },
    { rowId: 'lighting', source: 'free-item', freeItemPrefix: 'Подсветка' },
  ],
  cabinet: [
    { rowId: 'facades', source: 'material-payload', materialPriceField: 'facadePrice' },
    { rowId: 'facade-radius', source: 'free-item', freeItemPrefix: 'Фасад радиусный' },
    {
      rowId: 'vitrine',
      source: 'free-item',
      freeItemPrefix: 'Витрина в алюминиевом',
      anketaRowId: 'aluminum-glass-vitrine',
    },
    { rowId: 'rod', source: 'free-item', freeItemPrefix: 'Штанга' },
    { rowId: 'pantograph', source: 'free-item', freeItemPrefix: 'Пантограф' },
    { rowId: 'basket-pullout', source: 'free-item', freeItemPrefix: 'Корзина выдвижная' },
    { rowId: 'trousers', source: 'free-item', freeItemPrefix: 'Брючница' },
    { rowId: 'ironing-board', source: 'free-item', freeItemPrefix: 'Гладильная доска' },
    { rowId: 'shoe-rack', source: 'free-item', freeItemPrefix: 'Обувница' },
    {
      rowId: 'vacuum-holder',
      source: 'free-item',
      freeItemPrefix: 'Держатель для шланга пылесоса',
    },
    { rowId: 'lighting', source: 'free-item', freeItemPrefix: 'Подсветка' },
  ],
  'bath-tabletop': [
    { rowId: 'countertop', source: 'free-item', freeItemPrefix: 'Столешница' },
    { rowId: 'facades', source: 'material-payload', materialPriceField: 'facadePrice' },
    { rowId: 'facade-radius', source: 'free-item', freeItemPrefix: 'Фасад радиусный' },
    {
      rowId: 'vitrine',
      source: 'free-item',
      freeItemPrefix: 'Витрина в алюминиевом',
      anketaRowId: 'aluminum-glass-vitrine',
    },
    { rowId: 'metallic-legs', source: 'free-item', freeItemPrefix: 'Металлические опоры' },
    { rowId: 'basket-pullout', source: 'free-item', freeItemPrefix: 'Корзина выдвижная' },
    { rowId: 'lighting', source: 'free-item', freeItemPrefix: 'Подсветка' },
  ],
  'bath-shelf-above': [
    { rowId: 'facades', source: 'material-payload', materialPriceField: 'facadePrice' },
    { rowId: 'facade-radius', source: 'free-item', freeItemPrefix: 'Фасад радиусный' },
    { rowId: 'lighting', source: 'free-item', freeItemPrefix: 'Подсветка' },
  ],
  'bath-cabinet': [
    { rowId: 'facades', source: 'material-payload', materialPriceField: 'facadePrice' },
    { rowId: 'facade-radius', source: 'free-item', freeItemPrefix: 'Фасад радиусный' },
    { rowId: 'lighting', source: 'free-item', freeItemPrefix: 'Подсветка' },
    { rowId: 'laundry-basket', source: 'free-item', freeItemPrefix: 'Корзина для белья' },
  ],
  'tv-zone': [
    { rowId: 'facades', source: 'material-payload', materialPriceField: 'facadePrice' },
    { rowId: 'facade-radius', source: 'free-item', freeItemPrefix: 'Фасад радиусный' },
    {
      rowId: 'vitrine',
      source: 'free-item',
      freeItemPrefix: 'Витрина в алюминиевом',
      anketaRowId: 'aluminum-glass-vitrine',
    },
    { rowId: 'metallic-legs', source: 'free-item', freeItemPrefix: 'Металлические опоры' },
  ],
  balcony: [
    { rowId: 'rod', source: 'free-item', freeItemPrefix: 'Штанга' },
    { rowId: 'pantograph', source: 'free-item', freeItemPrefix: 'Пантограф' },
    { rowId: 'trousers', source: 'free-item', freeItemPrefix: 'Брючница' },
    { rowId: 'ironing-board', source: 'free-item', freeItemPrefix: 'Гладильная доска' },
    { rowId: 'shoe-rack', source: 'free-item', freeItemPrefix: 'Обувница' },
    {
      rowId: 'vacuum-holder',
      source: 'free-item',
      freeItemPrefix: 'Держатель для шланга пылесоса',
    },
    { rowId: 'lighting', source: 'free-item', freeItemPrefix: 'Подсветка' },
  ],
  other: [], // empty whitelist — no seed expected
};
